/**
 * Zustand store for initial retrieval progress (INITIAL_RETRIEVAL).
 *
 * Owned by RetrievalProgressInitialManager (headless component mounted outside Router).
 * The manager registers connect/disconnect here so any component can trigger the
 * initial fetch and subscribe to progress without holding the SSE subscription.
 *
 * Used by:
 * - RetrievalProgressInitialManager: registers fns, writes progress
 * - Metadata page / EditorLayout: calls connect() after createRetrieval()
 * - ActivityDropdown: shows initial fetch progress via activityStore
 */

import { create } from 'zustand';
import {
  getActiveOrgId,
  isActiveOrg,
  registerOrgScopedState,
  type OrgId,
} from '@/lib/org-scope';
import { devtools } from 'zustand/middleware';
import type { InitialPhaseDetails } from '@/dataProvider/retrievals';

// ============================================================================
// Types
// ============================================================================

export type InitialRetrievalPhase = 'discovery' | 'download' | 'processing' | 'enrichment';

export interface RetrievalProgressInitialState {
  retrievalId: string | null;
  status: string | null;
  message: string | null;
  progressPercentage: number;
  currentPhase: InitialRetrievalPhase | null;
  totalItems: number;
  processedItems: number;
  phaseDetails: InitialPhaseDetails | null;
  isConnected: boolean;
  isComplete: boolean;
  connectionError: string | null;
}

export interface RetrievalProgressInitialSetProgress extends Partial<RetrievalProgressInitialState> {}

interface RetrievalProgressInitialStoreState extends RetrievalProgressInitialState {
  /**
   * The organization whose initial fetch the open stream describes, captured at
   * {@link connect} time. Bookkeeping only — see `retrievalProgressStore` for why
   * every progress write is checked against it.
   */
  streamOrgId: OrgId | null;

  /**
   * Initial fetches whose streams were torn down by an org switch, keyed by the
   * organization that owns them. Taken by the manager on the way back in.
   */
  parkedByOrg: Map<string, string>;

  _connectFn: ((retrievalId: string) => void) | null;
  _disconnectFn: (() => void) | null;

  setConnectFn: (fn: (retrievalId: string) => void) => void;
  setDisconnectFn: (fn: () => void) => void;

  connect: (retrievalId: string) => void;
  disconnect: () => void;

  setProgress: (data: RetrievalProgressInitialSetProgress) => void;
  reset: () => void;

  /** Take the initial fetch parked for `orgId`, if any, and forget it. */
  takeParkedRetrievalId: (orgId: OrgId | null) => string | null;

  /**
   * Park the previous organization's fetch so returning can resume it, then tear
   * its stream down and clear what it published.
   */
  _stopForOrgChange: (previousOrgId: OrgId | null) => void;
}

// ============================================================================
// Initial state
// ============================================================================

const initialProgressState: RetrievalProgressInitialState = {
  retrievalId: null,
  status: null,
  message: null,
  progressPercentage: 0,
  currentPhase: null,
  totalItems: 0,
  processedItems: 0,
  phaseDetails: null,
  isConnected: false,
  isComplete: false,
  connectionError: null,
};

// ============================================================================
// Store
// ============================================================================

/** See `retrievalProgressStore.isStreamOnScreen`. */
function isStreamOnScreen(streamOrgId: OrgId | null): boolean {
  return streamOrgId === null || isActiveOrg(streamOrgId);
}

export const useRetrievalProgressInitialStore = create<RetrievalProgressInitialStoreState>()(
  devtools(
    (set, get) => ({
      ...initialProgressState,
      streamOrgId: null,
      parkedByOrg: new Map(),
      _connectFn: null,
      _disconnectFn: null,

      setConnectFn: (fn) => set({ _connectFn: fn }),
      setDisconnectFn: (fn) => set({ _disconnectFn: fn }),

      connect: (retrievalId) => {
        const fn = get()._connectFn;
        if (fn) {
          set({ retrievalId, streamOrgId: getActiveOrgId() });
          fn(retrievalId);
        } else {
          console.warn('[retrievalProgressInitialStore] connect called before manager mounted');
        }
      },

      disconnect: () => {
        const fn = get()._disconnectFn;
        if (fn) fn();
      },

      setProgress: (data) => {
        // Org A's initial fetch must not draw "Initial sync..." over Org B's bar.
        if (!isStreamOnScreen(get().streamOrgId)) return;
        set((state) => ({ ...state, ...data }));
      },

      reset: () => set({ ...initialProgressState, streamOrgId: null }),

      takeParkedRetrievalId: (orgId) => {
        if (!orgId) return null;
        const parkedId = get().parkedByOrg.get(orgId);
        if (!parkedId) return null;
        const remaining = new Map(get().parkedByOrg);
        remaining.delete(orgId);
        set({ parkedByOrg: remaining });
        return parkedId;
      },

      _stopForOrgChange: (previousOrgId) => {
        const { retrievalId, isComplete, parkedByOrg } = get();

        const parked = new Map(parkedByOrg);
        if (retrievalId && !isComplete && previousOrgId) parked.set(previousOrgId, retrievalId);

        get()._disconnectFn?.();
        set({ ...initialProgressState, streamOrgId: previousOrgId, parkedByOrg: parked });
      },
    }),
    { name: 'retrieval-progress-initial-store' }
  )
);

// ── Organization scoping ─────────────────────────────────────────────────────
//
// As with retrievalProgressStore: one stream, stamped with the selected org's id
// at connect time, so it is torn down on a switch — and the fetch it was
// following is parked under its organization, to be reconnected to when the user
// returns.
registerOrgScopedState({
  name: 'retrievalProgressInitialStore',
  onOrgChange: (_nextOrgId, previousOrgId) => {
    useRetrievalProgressInitialStore.getState()._stopForOrgChange(previousOrgId);
  },
  onSignOut: () => {
    const store = useRetrievalProgressInitialStore.getState();
    store.disconnect();
    store.reset();
    useRetrievalProgressInitialStore.setState({ parkedByOrg: new Map() });
  },
});
